import Decimal from 'decimal.js';
import provincesConfig from '../config/provinces.json';
import entitiesConfig from '../config/entities.json';
import { Expense, Session } from '../types/models';

// Configure Decimal.js for financial calculations
Decimal.config({ precision: 10, rounding: Decimal.ROUND_HALF_UP });

export interface ProvinceConfig {
  scheme: string;
  rate: number;
  pstv?: number;
  rstv?: number;
  pstvRecoverable?: boolean;
  rstvRecoverable?: boolean;
  ridesTaxable: boolean;
  name: string;
}

export interface EntityConfig {
  itc: {
    MEALS: string | boolean;
    ENTERTAIN: string | boolean;
    PHONE: string;
    INSURANCE: string;
    LEASE_PAYMENT: boolean;
    LOAN_INTEREST: boolean;
    TAX_SERVICES: boolean;
  };
  incomeHints: {
    MEALS: number;
  };
}

/**
 * Pure function to calculate GST/HST collected from fare and tips
 */
export const calcGSTCollected = (
  fare: number,
  tips: number,
  rate: number,
  incomeIncludesGST = false
): number => {
  const fareDecimal = new Decimal(fare);
  const tipsDecimal = new Decimal(tips);
  const rateDecimal = new Decimal(rate);

  if (incomeIncludesGST) {
    // Extract GST from tax-inclusive amount
    const totalInclusive = fareDecimal.plus(tipsDecimal);
    return totalInclusive.mul(rateDecimal).div(new Decimal(1).plus(rateDecimal)).toNumber();
  } else {
    // Calculate GST on pre-tax amount
    const totalPreTax = fareDecimal.plus(tipsDecimal);
    return totalPreTax.mul(rateDecimal).toNumber();
  }
};

/**
 * Pure function to calculate gross income including GST
 */
export const calcGrossIncomeWithGST = (
  fare: number,
  tips: number,
  rate: number,
  incomeIncludesGST = false
): number => {
  const fareDecimal = new Decimal(fare);
  const tipsDecimal = new Decimal(tips);
  const rateDecimal = new Decimal(rate);

  if (incomeIncludesGST) {
    return fareDecimal.plus(tipsDecimal).toNumber();
  } else {
    const totalPreTax = fareDecimal.plus(tipsDecimal);
    return totalPreTax.mul(new Decimal(1).plus(rateDecimal)).toNumber();
  }
};

/**
 * Get province configuration
 */
export const getProvinceConfig = (provinceCode: string): ProvinceConfig => {
  const config = (provincesConfig as Record<string, ProvinceConfig>)[provinceCode];
  if (!config) {
    throw new Error(`Invalid province code: ${provinceCode}`);
  }
  return config;
};

/**
 * Get entity configuration
 */
export const getEntityConfig = (entityType: string): EntityConfig => {
  const config = (entitiesConfig as Record<string, EntityConfig>)[entityType];
  if (!config) {
    throw new Error(`Invalid entity type: ${entityType}`);
  }
  return config;
};

/**
 * Compute meal/entertainment treatment per CRA 50% rule
 */
export const computeMealTreatment = (
  expense: Expense,
  _provinceCode: string
): { deductible: number; itc: number } => {
  // Not qualified → no deduction, no ITC
  if (!expense.qualifies50Pct) {
    return { deductible: 0, itc: 0 };
  }

  const grossMealCost = new Decimal(expense.amount);

  // Income tax: 50% of gross meal cost (net + GST/HST + PST/RST where applicable)
  const deductible = grossMealCost.mul(0.5).toNumber();

  // ITC: 50% of GST/HST paid (from gstPaid field, which should be the recoverable tax portion)
  const itc = new Decimal(expense.gstPaid).mul(0.5).toNumber();

  return { deductible, itc };
};

/**
 * Tax Engine class for comprehensive tax calculations
 */
export class TaxEngine {
  private provinceCode: string;
  private provinceConfig: ProvinceConfig;
  private entityConfig: EntityConfig;

  constructor(provinceCode: string, entityType: string = 'SOLE_PROP') {
    this.provinceCode = provinceCode;
    this.provinceConfig = getProvinceConfig(provinceCode);
    this.entityConfig = getEntityConfig(entityType);
  }

  /**
   * Calculate GST/HST from a pre-tax amount
   */
  calculateGSTFromPreTax(preTaxAmount: number): number {
    const amount = new Decimal(preTaxAmount);
    const rate = new Decimal(this.provinceConfig.rate);
    return amount.mul(rate).toNumber();
  }

  /**
   * Calculate GST/HST from a total amount (tax-inclusive)
   */
  calculateGSTFromTotal(totalAmount: number): number {
    const total = new Decimal(totalAmount);
    const rate = new Decimal(this.provinceConfig.rate);
    const divisor = new Decimal(1).plus(rate);
    return total.mul(rate).div(divisor).toNumber();
  }

  /**
   * Calculate pre-tax amount from total (tax-inclusive)
   */
  calculatePreTaxFromTotal(totalAmount: number): number {
    const total = new Decimal(totalAmount);
    const rate = new Decimal(this.provinceConfig.rate);
    const divisor = new Decimal(1).plus(rate);
    return total.div(divisor).toNumber();
  }

  /**
   * Calculate claimable ITC for an expense based on entity and province rules
   */
  calculateClaimableITC(expense: Expense): number {
    const gstPaid = new Decimal(expense.gstPaid);
    const businessUsePct = new Decimal(expense.businessUsePct);

    // Oct 24, 2025 — Loan interest: no ITC (exempt financial service)
    if (expense.category === 'loan-interest') {
      return 0;
    }

    // Oct 24, 2025 — Meals / Entertainment — App policy: ITC = 0 always
    if (expense.category === 'meals' || expense.category === 'entertainment') {
      return 0;
    }

    // Apply entity-specific ITC rules for other categories
    let claimableRate = businessUsePct.div(100);

    // Apply specific rules based on expense category
    switch (expense.category) {
      case 'phone-data':
        if (this.entityConfig.itc.PHONE === 'partial') {
          // Use the business use percentage as specified
          // claimableRate already set above
        }
        break;
      case 'insurance':
        if (this.entityConfig.itc.INSURANCE === 'serviceFeeGSTOnly') {
          // Only service fees are claimable, not the premium itself
          // This would need additional logic to separate service fees
          // For now, use the business percentage
        }
        break;
      case 'lease-payment':
        // Eligible, scale by business use
        break;
      case 'tax-services':
        // Eligible as services with GST/HST charged
        break;
    }

    // Apply province-specific ITC rules
    let gstClaimable = gstPaid.mul(claimableRate);

    // For BC/MB GST+PST provinces, ITC is only recoverable on GST portion (5%), not PST/RST
    if (
      this.provinceConfig.scheme === 'GST' &&
      (this.provinceConfig.pstv || this.provinceConfig.rstv)
    ) {
      // For tax-inclusive amounts, calculate only the GST portion
      if (expense.gstPaid > 0 && expense.amount > 0) {
        // GST portion = amount * 0.05 / 1.05 (for 5% GST + 7% PST)
        const totalRate = new Decimal(1)
          .plus(this.provinceConfig.rate)
          .plus(this.provinceConfig.pstv || this.provinceConfig.rstv || 0);
        const gstPortion = new Decimal(expense.amount).mul(this.provinceConfig.rate).div(totalRate);
        gstClaimable = gstPortion.mul(claimableRate);
      }
    }

    return gstClaimable.toNumber();
  }

  /**
   * Calculate deductible amount for income tax purposes
   */
  calculateDeductibleAmount(expenseAmount: number, businessUsePct: number): number {
    const amount = new Decimal(expenseAmount);
    const pct = new Decimal(businessUsePct);
    return amount.mul(pct).div(100).toNumber();
  }

  /**
   * Get the applicable tax rate for the province
   */
  getTaxRate(): number {
    return this.provinceConfig.rate;
  }

  /**
   * Get tax scheme name (GST, HST, GST+PST)
   */
  getTaxSchemeName(): string {
    return this.provinceConfig.scheme;
  }

  /**
   * Get the tax rate as a percentage for display
   */
  getTaxRatePercent(): number {
    return this.provinceConfig.rate * 100;
  }

  /**
   * Get province name
   */
  getProvinceName(): string {
    return this.provinceConfig.name;
  }
}

/**
 * CRA Line Calculator using config-driven rates
 */
export class CRALineCalculator {
  /**
   * Calculate all CRA GST/HST return lines
   */
  static calculateGSTLines(
    sessions: Session[],
    expenses: Expense[],
    provinceCode: string
  ): {
    line90: number;
    line91: number;
    line101: number;
    line103: number;
    line106: number;
    line108: number;
    line109: number;
    line115: number;
  } {
    const taxEngine = new TaxEngine(provinceCode);

    // Line 103: GST collected on taxable sales
    const line103 = sessions.reduce(
      (sum, session) => new Decimal(sum).plus(session.gstCollected).toNumber(),
      0
    );

    // Line 106: Input Tax Credits (ITCs)
    const line106 = expenses.reduce(
      (sum, expense) => new Decimal(sum).plus(taxEngine.calculateClaimableITC(expense)).toNumber(),
      0
    );

    // Line 90: Total taxable sales (including GST)
    const grossEarnings = sessions.reduce(
      (sum, session) => new Decimal(sum).plus(session.grossEarnings).toNumber(),
      0
    );
    const line90 = new Decimal(grossEarnings).plus(line103).toNumber();

    // Line 91: Exempt supplies (typically 0 for ride-share)
    const line91 = 0;

    // Line 101: Total sales and other revenue
    const line101 = new Decimal(line90).plus(line91).toNumber();

    // Line 108: Adjustments (typically 0)
    const line108 = 0;

    // Line 109: Net Tax (GST collected - ITCs + adjustments)
    const line109 = new Decimal(line103).minus(line106).plus(line108).toNumber();

    // Line 115: Amount owing or refund (same as line 109, rounded to nearest dollar)
    const line115 = Math.round(line109);

    return {
      line90,
      line91,
      line101,
      line103,
      line106,
      line108,
      line109,
      line115,
    };
  }

  /**
   * Calculate income tax deductions summary
   */
  static calculateIncomeTaxSummary(
    sessions: Session[],
    expenses: Expense[],
    _provinceCode: string
  ): {
    totalIncome: number;
    totalExpenses: number;
    totalDeductions: number;
    netIncome: number;
  } {
    const totalIncome = sessions.reduce(
      (sum, session) => new Decimal(sum).plus(session.grossEarnings).toNumber(),
      0
    );

    const totalExpenses = expenses.reduce(
      (sum, expense) => new Decimal(sum).plus(expense.amount).toNumber(),
      0
    );

    const totalDeductions = expenses.reduce((sum, expense) => {
      let deductible: number;

      // Oct 24, 2025 — Meals / Entertainment — App policy: ITC = 0 always
      if (expense.category === 'meals' || expense.category === 'entertainment') {
        const meetsNarrowException =
          Boolean(expense.mealContext?.overnight) &&
          (expense.mealContext?.distanceMiles ?? 0) >= 100 &&
          !!expense.mealContext?.hotelReceiptUrl &&
          !!expense.mealContext?.meetingDetails;

        const deductibleGross = expense.amount; // includes PST/RST; deduction is on gross
        deductible = meetsNarrowException
          ? new Decimal(deductibleGross).mul(0.5).mul(expense.businessUsePct).div(100).toNumber()
          : 0;
      } else if (expense.category === 'loan-interest') {
        // Oct 24, 2025 — Loan interest: always deductible (no ITC, but deductible as interest expense)
        deductible = new Decimal(expense.amount).mul(expense.businessUsePct).div(100).toNumber();
      } else {
        // Standard calculation: amount * businessUsePct / 100
        deductible = new Decimal(expense.amount).mul(expense.businessUsePct).div(100).toNumber();
      }

      return new Decimal(sum).plus(deductible).toNumber();
    }, 0);

    const netIncome = new Decimal(totalIncome).minus(totalDeductions).toNumber();

    return {
      totalIncome,
      totalExpenses,
      totalDeductions,
      netIncome,
    };
  }
}

/**
 * Utility functions for common tax calculations
 */
export const taxUtils = {
  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  },

  /**
   * Format percentage for display
   */
  formatPercentage(percentage: number): string {
    return `${percentage.toFixed(1)}%`;
  },

  /**
   * Round to 2 decimal places for currency
   */
  roundCurrency(amount: number): number {
    return Math.round(amount * 100) / 100;
  },

  /**
   * Round to nearest dollar for CRA reporting
   */
  roundToDollar(amount: number): number {
    return Math.round(amount);
  },

  /**
   * Calculate percentage of total
   */
  calculatePercentage(part: number, total: number): number {
    if (total === 0) return 0;
    return new Decimal(part).div(total).mul(100).toNumber();
  },

  /**
   * Validate GST number format (9 digits)
   */
  validateGSTNumber(gstNumber: string): boolean {
    const cleaned = gstNumber.replace(/\D/g, '');
    return cleaned.length === 9;
  },

  /**
   * Format GST number for display
   */
  formatGSTNumber(gstNumber: string): string {
    const cleaned = gstNumber.replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)}`;
    }
    return gstNumber;
  },
};
